import type { ReactNode } from 'react'
import { cn } from '@quickmd-llc/design-system'

type Props = {
  /** Breadcrumb element (e.g. `<Breadcrumb crumbs={...} />`). */
  breadcrumb: ReactNode
  /** Page title — rendered as an `<h1>` only when provided. */
  title?: ReactNode
  /** Action buttons or badges placed to the right of the title. */
  titleActions?: ReactNode
  /** Page body content rendered below the header. */
  children: ReactNode
  /** Fixed bottom action bar. */
  footer?: ReactNode
  /**
   * When true the outer container gets `min-w-[770px]` (view-service / new-service need this;
   * new-members does not — its responsive layout breaks with the min-width constraint).
   */
  constrainMinWidth?: boolean
}

/**
 * Shared full-screen scaffold used by edit/create management pages:
 *   outer container → breadcrumb + title row → page body → fixed footer
 *
 * Each consumer keeps its exact classes; divergences are represented via props rather than
 * homogenising visually (see `constrainMinWidth`).
 */
export function EditPageShell({ breadcrumb, title, titleActions, children, footer, constrainMinWidth = false }: Props) {
  return (
    <div className='flex flex-1 flex-col'>
      <div
        className={cn(
          // flex-1 (not min-h-full) so the grey body fills the viewport down to the fixed footer;
          // otherwise the AuthenticatedLayout's white background shows through below short content.
          'box-border flex w-full flex-1 flex-col gap-sp-2 bg-deep-navy-5 px-sp-3 py-sp-5 pb-[100px]',
          constrainMinWidth && 'min-w-[770px]',
        )}>
        {(breadcrumb !== undefined || title !== undefined) && (
          <div className='mb-sp-1'>
            {breadcrumb}
            {title !== undefined && (
              <div className='flex items-center justify-between gap-sp-2'>
                <div className='flex items-center gap-sp-1.5'>{title}</div>
                {titleActions !== undefined && (
                  <div className='flex shrink-0 items-center gap-sp-1.5'>{titleActions}</div>
                )}
              </div>
            )}
          </div>
        )}
        {children}
      </div>
      {footer}
    </div>
  )
}
